// Settings (Company + Change Password), Billing (Subscription overview + Invoices + Paystack upgrade), Reports (Sales/Purchase/Stock/Profit)
import { useEffect, useState, useRef } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Save, KeyRound, Building2, CreditCard, BarChart3, TrendingUp, Package, ShoppingCart, Receipt, Download, ArrowUpCircle, Check, Image as ImageIcon, Upload, Banknote, Truck } from "lucide-react";
import { api, API, getToken, TENANT_KEY } from "../../../lib/auth";
import { Card, PageHeader, PrimaryButton, Field, TextInput, Select, SectionHeader, Toggle } from "../products/_shared";
import { useConfirm } from "../../../components/ConfirmDialog";
import { fmtMoney as fmt } from "../../../lib/currency";

// ────────────────────────── SETTINGS ──────────────────────────
export function TenantSettings() {
  const [form, setForm] = useState(null);
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "" });
  const [logoBusy, setLogoBusy] = useState(false);
  const fileRef = useRef(null);
  const reload = () => api.get("/t/settings/company").then((r) => setForm(r.data));
  useEffect(() => { reload(); }, []);
  if (!form) return <div className="p-8 text-black/45">Loading…</div>;
  const upd = (patch) => setForm((f) => ({ ...f, ...patch }));
  const save = async () => {
    try {
      // Strip masked secret out of payload — backend handles this too but keeps payload clean
      const payload = { ...form };
      delete payload.paystack_secret_key_masked;
      delete payload.paystack_secret_key_set;
      await api.patch("/t/settings/company", payload);
      toast.success("Settings saved");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const changePw = async () => {
    if (pwForm.new_password.length < 8) return toast.error("New password must be 8+ chars");
    try {
      await api.post("/t/settings/change-password", pwForm);
      toast.success("Password updated"); setPwForm({ current_password: "", new_password: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const onLogoFile = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) return toast.error("Logo too large (max 2MB)");
    setLogoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch(`${API}/t/settings/upload-logo`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      });
      if (!r.ok) throw new Error((await r.json()).detail || "Upload failed");
      const data = await r.json();
      upd({ logo_url: data.url });
      toast.success("Logo uploaded");
    } catch (e) { toast.error(e.message || "Upload failed"); }
    finally { setLogoBusy(false); }
  };
  const removeLogo = async () => {
    try {
      await api.patch("/t/settings/company", { logo_url: "" });
      upd({ logo_url: "" });
      toast.success("Logo removed");
    } catch (e) { toast.error("Failed"); }
  };
  return (
    <div data-testid="settings-page" className="p-6 lg:p-8 space-y-5">
      <PageHeader title="Settings" breadcrumb="Settings" actions={
        <PrimaryButton icon={Save} onClick={save} testid="set-save">Save Settings</PrimaryButton>
      } />
      <div className="grid lg:grid-cols-3 gap-5">
        <Card className="p-5 lg:col-span-2">
          <SectionHeader icon={Building2} title="Company Information" />
          <div className="grid md:grid-cols-2 gap-3">
            <Field lbl="Company Name"><TextInput value={form.company_name} onChange={(v) => upd({ company_name: v })} testid="set-company-name" /></Field>
            <Field lbl="Your Business Link" hint="Your workspace URL slug (read-only)">
              <div className="px-3 py-2.5 rounded-lg border border-black/10 bg-[#FAFBFB] text-sm font-mono text-black/70" data-testid="set-subdomain">brivo360.com/t/{form.subdomain}/login</div>
            </Field>
            <Field lbl="Phone"><TextInput value={form.phone} onChange={(v) => upd({ phone: v })} testid="set-phone" /></Field>
            <Field lbl="Email"><TextInput value={form.email} onChange={(v) => upd({ email: v })} testid="set-email" /></Field>
            <Field lbl="Country"><TextInput value={form.country} onChange={(v) => upd({ country: v })} /></Field>
            <Field lbl="City"><TextInput value={form.city} onChange={(v) => upd({ city: v })} /></Field>
            <Field lbl="Currency"><Select value={form.currency} onChange={(v) => upd({ currency: v })} options={["NGN", "USD", "EUR", "GBP", "KES", "ZAR", "GHS"].map((c) => ({ value: c, label: c }))} /></Field>
            <Field lbl="Timezone"><TextInput value={form.timezone} onChange={(v) => upd({ timezone: v })} /></Field>
            <Field lbl="Tax Number"><TextInput value={form.tax_number} onChange={(v) => upd({ tax_number: v })} /></Field>
            <Field lbl="Fiscal Year Start (MM-DD)"><TextInput value={form.fiscal_year_start} onChange={(v) => upd({ fiscal_year_start: v })} /></Field>
            <Field lbl="Address" className="md:col-span-2">
              <textarea value={form.address || ""} onChange={(e) => upd({ address: e.target.value })}
                className="w-full px-3 py-2.5 rounded-lg border border-black/15 text-sm min-h-[70px]" />
            </Field>
          </div>
        </Card>

        <Card className="p-5">
          <SectionHeader icon={ImageIcon} title="Company Logo" hint="Shown on your storefront, invoices & receipts" />
          <div className="flex flex-col items-center gap-3" data-testid="logo-block">
            <div className="w-32 h-32 rounded-2xl bg-[#FAFBFB] border border-dashed border-black/15 flex items-center justify-center overflow-hidden">
              {form.logo_url ? (
                <img src={`${process.env.REACT_APP_BACKEND_URL}${form.logo_url}`} alt="logo" className="max-w-full max-h-full object-contain" data-testid="logo-preview" />
              ) : (
                <div className="text-xs text-black/40 text-center px-3">No logo uploaded</div>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" data-testid="logo-file"
              onChange={(e) => onLogoFile(e.target.files?.[0])} className="hidden" />
            <div className="flex gap-2 w-full">
              <button onClick={() => fileRef.current?.click()} disabled={logoBusy} data-testid="logo-upload-btn"
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-[#1FA868] text-white text-sm font-bold disabled:opacity-60">
                <Upload size={13} /> {logoBusy ? "Uploading…" : (form.logo_url ? "Change" : "Upload")}
              </button>
              {form.logo_url && (
                <button onClick={removeLogo} data-testid="logo-remove-btn"
                  className="px-3 py-2 rounded-lg border border-black/10 text-sm font-semibold text-black/70 hover:bg-black/5">Remove</button>
              )}
            </div>
            <div className="text-[11px] text-black/45 text-center">PNG / JPG / WebP / SVG · Max 2MB</div>
          </div>
        </Card>

        <Card className="p-5 lg:col-span-3">
          <SectionHeader icon={CreditCard} title="Storefront Payment Methods" hint="Choose how customers pay during online checkout" />
          <div className="grid lg:grid-cols-3 gap-5">
            {/* Paystack */}
            <div className={`p-4 rounded-xl border ${form.paystack_enabled ? "border-[#1FA868] bg-[#F0FAF3]" : "border-black/10 bg-white"}`} data-testid="pm-paystack">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <CreditCard size={16} className="text-[#1FA868]" />
                  <span className="font-bold text-sm">Paystack</span>
                </div>
                <Toggle value={!!form.paystack_enabled} onChange={(v) => upd({ paystack_enabled: v })} testid="pm-paystack-toggle" />
              </div>
              <p className="text-[11px] text-black/55 mb-3">Use your own Paystack account so payments land directly in your bank.</p>
              <Field lbl="Public Key">
                <TextInput value={form.paystack_public_key} onChange={(v) => upd({ paystack_public_key: v })} placeholder="pk_live_… or pk_test_…" testid="pm-paystack-pk" />
              </Field>
              <div className="mt-3">
                <Field lbl="Secret Key" hint={form.paystack_secret_key_set ? `Stored: ${form.paystack_secret_key_masked} — leave blank to keep` : ""}>
                  <TextInput type="password" value={form.paystack_secret_key || ""} onChange={(v) => upd({ paystack_secret_key: v })} placeholder="sk_live_… or sk_test_…" testid="pm-paystack-sk" />
                </Field>
              </div>
              <a href="https://dashboard.paystack.com/#/settings/developer" target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1 mt-3 text-[11px] text-[#1FA868] font-semibold hover:underline">Where do I find my keys? →</a>
            </div>

            {/* Bank Transfer */}
            <div className={`p-4 rounded-xl border ${form.bank_transfer_enabled ? "border-[#1FA868] bg-[#F0FAF3]" : "border-black/10 bg-white"}`} data-testid="pm-bank">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Banknote size={16} className="text-[#1FA868]" />
                  <span className="font-bold text-sm">Bank Transfer</span>
                </div>
                <Toggle value={!!form.bank_transfer_enabled} onChange={(v) => upd({ bank_transfer_enabled: v })} testid="pm-bank-toggle" />
              </div>
              <Field lbl="Bank Name"><TextInput value={form.bank_name} onChange={(v) => upd({ bank_name: v })} testid="pm-bank-name" /></Field>
              <div className="mt-3"><Field lbl="Account Holder"><TextInput value={form.bank_account_holder} onChange={(v) => upd({ bank_account_holder: v })} testid="pm-bank-holder" /></Field></div>
              <div className="mt-3"><Field lbl="Account Number"><TextInput value={form.bank_account_number} onChange={(v) => upd({ bank_account_number: v })} testid="pm-bank-number" /></Field></div>
              <div className="mt-3"><Field lbl="Instructions for buyer">
                <textarea value={form.bank_instructions || ""} onChange={(e) => upd({ bank_instructions: e.target.value })} rows={2}
                  data-testid="pm-bank-instructions"
                  className="w-full px-3 py-2.5 rounded-lg border border-black/15 text-xs" placeholder="e.g. Use order number as the reference" />
              </Field></div>
            </div>

            {/* Pay on Delivery */}
            <div className={`p-4 rounded-xl border ${form.cod_enabled ? "border-[#1FA868] bg-[#F0FAF3]" : "border-black/10 bg-white"}`} data-testid="pm-cod">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Truck size={16} className="text-[#1FA868]" />
                  <span className="font-bold text-sm">Pay on Delivery</span>
                </div>
                <Toggle value={!!form.cod_enabled} onChange={(v) => upd({ cod_enabled: v })} testid="pm-cod-toggle" />
              </div>
              <p className="text-[11px] text-black/55 mb-3">Customer pays the courier in cash when the goods arrive.</p>
              <Field lbl="Instructions for buyer">
                <textarea value={form.cod_instructions || ""} onChange={(e) => upd({ cod_instructions: e.target.value })} rows={4}
                  data-testid="pm-cod-instructions"
                  className="w-full px-3 py-2.5 rounded-lg border border-black/15 text-xs" placeholder="e.g. Have the exact amount ready in cash" />
              </Field>
            </div>
          </div>
          <div className="mt-5 flex justify-end">
            <PrimaryButton icon={Save} onClick={save} testid="set-save-payment">Save Payment Methods</PrimaryButton>
          </div>
        </Card>

        <Card className="p-5">
          <SectionHeader icon={KeyRound} title="Change Password" />
          <div className="space-y-3">
            <Field lbl="Current Password" required><TextInput type="password" value={pwForm.current_password} onChange={(v) => setPwForm((f) => ({ ...f, current_password: v }))} testid="set-cur-pw" /></Field>
            <Field lbl="New Password" required hint="Must be 8+ chars"><TextInput type="password" value={pwForm.new_password} onChange={(v) => setPwForm((f) => ({ ...f, new_password: v }))} testid="set-new-pw" /></Field>
          </div>
          <div className="mt-5">
            <PrimaryButton icon={Save} onClick={changePw} testid="set-pw-save">Update Password</PrimaryButton>
          </div>
        </Card>
      </div>
    </div>
  );
}

// ────────────────────────── BILLING ──────────────────────────
export function TenantBilling() {
  const [d, setD] = useState(null);
  const [allPlans, setAllPlans] = useState([]);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [showPlans, setShowPlans] = useState(false);
  const [cycle, setCycle] = useState("monthly");
  const [busy, setBusy] = useState(false);
  const [params] = useSearchParams();
  const confirm = useConfirm();

  const load = () => axios.get(`${API}/t/billing/overview`, { headers: { Authorization: `Bearer ${getToken()}` } }).then((r) => setD(r.data));

  useEffect(() => {
    load();
    axios.get(`${API}/plans`).then((r) => setAllPlans(r.data || []));
    axios.get(`${API}/payment-info`).then((r) => setPaymentInfo(r.data));
  }, []);

  // Handle Paystack callback (?reference=…)
  useEffect(() => {
    const ref = params.get("reference") || params.get("trxref");
    if (!ref) return;
    setBusy(true);
    axios.get(`${API}/payments/paystack/verify/${ref}`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => {
        if (r.data?.status === "success") {
          toast.success("Payment verified — plan upgraded!");
          // Refresh stored tenant blob via /auth/me
          axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${getToken()}` } }).then((m) => {
            if (m.data?.tenant) localStorage.setItem(TENANT_KEY, JSON.stringify(m.data.tenant));
            setTimeout(() => window.location.href = window.location.pathname, 800);
          });
        } else {
          toast.error("Payment not verified");
        }
      })
      .finally(() => setBusy(false));
  }, [params]);

  if (!d) return <div className="p-8 text-black/45">Loading…</div>;
  const { tenant: t, plan, usage, invoices } = d;
  const pct = (used, limit) => limit === -1 ? 0 : Math.min(100, Math.round((used / (limit || 1)) * 100));
  const features = plan?.features || [];
  const limits = plan?.limits || {};
  const usageRows = [
    { lbl: "Products", used: usage.products, limit: limits.products },
    { lbl: "Users", used: usage.users, limit: limits.users },
    { lbl: "Customers", used: usage.customers, limit: limits.customers },
    { lbl: "Suppliers", used: usage.suppliers, limit: limits.suppliers },
  ];

  const startUpgrade = async (selectedPlan) => {
    if (!(await confirm({ title: `Upgrade to ${selectedPlan.name}?`, message: `You will be charged ${fmt(cycle === "monthly" ? selectedPlan.price_monthly : selectedPlan.price_yearly)} via Paystack.` }))) return;
    setBusy(true);
    try {
      const callback = `${window.location.origin}/t/${t.subdomain}/billing`;
      const r = await axios.post(`${API}/payments/paystack/init`, {
        plan_id: selectedPlan.id,
        billing_cycle: cycle,
        callback_url: callback,
      }, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (r.data?.free) {
        toast.success("Free plan applied!");
        setTimeout(() => window.location.reload(), 600);
      } else if (r.data?.authorization_url) {
        window.location.href = r.data.authorization_url;
      } else {
        toast.error("Could not initialize payment");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Payment init failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="billing-page" className="p-6 lg:p-8">
      <PageHeader title="Billing" breadcrumb="Billing" />
      {busy && <div className="fixed inset-0 bg-white/70 backdrop-blur-sm z-[55] flex items-center justify-center"><div className="text-center"><div className="w-12 h-12 border-4 border-[#1FA868]/30 border-t-[#1FA868] rounded-full animate-spin mx-auto" /><div className="mt-4 text-sm font-bold text-[#1FA868]">Processing payment…</div></div></div>}
      <div className="grid lg:grid-cols-3 gap-5 mb-6">
        <Card className="lg:col-span-2 p-6 bg-gradient-to-br from-[#1FA868] to-[#16834D] text-white">
          <div className="text-xs uppercase opacity-80">Current Plan</div>
          <div className="mt-1 flex items-baseline gap-2">
            <div className="text-4xl font-extrabold">{t.plan_name}</div>
            <div className="text-lg opacity-80">· {t.billing_cycle}</div>
          </div>
          <div className="text-3xl font-extrabold mt-2">{fmt(t.amount)}<span className="text-sm opacity-70">/{t.billing_cycle === "monthly" ? "mo" : "yr"}</span></div>
          <div className="mt-5 text-xs opacity-80 uppercase">Status</div>
          <div className="text-lg font-bold capitalize">{t.status}</div>
          <div className="mt-5 flex gap-2">
            <button onClick={() => setShowPlans(!showPlans)} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-white text-[#1FA868] text-sm font-bold hover:bg-white/90" data-testid="billing-upgrade">
              <ArrowUpCircle size={14} /> {showPlans ? "Hide Plans" : "Upgrade Plan"}
            </button>
          </div>
        </Card>
        <Card className="p-5">
          <div className="text-xs uppercase text-black/55 mb-3">Plan Features</div>
          <ul className="text-sm space-y-2">
            {features.slice(0, 8).map((f, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="text-[#1FA868] mt-0.5">✓</span>
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </Card>
      </div>

      {showPlans && (
        <Card className="p-5 mb-6" data-testid="plan-picker">
          <div className="flex items-center justify-between mb-4">
            <SectionHeader icon={CreditCard} title="Choose a Plan" />
            <div className="flex bg-[#FAFBFB] rounded-lg p-1">
              <button onClick={() => setCycle("monthly")} className={`px-3 py-1.5 rounded text-xs font-bold ${cycle === "monthly" ? "bg-[#1FA868] text-white" : "text-black/60"}`}>Monthly</button>
              <button onClick={() => setCycle("yearly")} className={`px-3 py-1.5 rounded text-xs font-bold ${cycle === "yearly" ? "bg-[#1FA868] text-white" : "text-black/60"}`}>Yearly</button>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {allPlans.map((p) => {
              const price = cycle === "monthly" ? p.price_monthly : p.price_yearly;
              const isCurrent = p.id === t.plan_id;
              return (
                <div key={p.id} className={`rounded-2xl p-5 border-2 ${isCurrent ? "border-[#1FA868] bg-[#E8F5E9]" : "border-black/5"}`}>
                  <div className="font-extrabold text-lg">{p.name}</div>
                  <div className="text-2xl font-extrabold mt-2">{price === 0 ? "Free" : fmt(price)}</div>
                  <div className="text-xs text-black/55">{cycle === "monthly" ? "per month" : "per year"}</div>
                  <ul className="text-xs space-y-1 mt-3 min-h-[100px]">
                    {(p.features || []).slice(0, 5).map((f, i) => <li key={i} className="flex gap-1.5"><Check size={11} className="text-[#1FA868] mt-0.5" /> {f}</li>)}
                  </ul>
                  <button
                    onClick={() => !isCurrent && startUpgrade(p)}
                    disabled={isCurrent || busy}
                    data-testid={`plan-select-${p.id}`}
                    className={`w-full mt-4 py-2 rounded-lg text-sm font-bold ${isCurrent ? "bg-black/5 text-black/45" : "bg-[#1FA868] text-white hover:opacity-95"}`}>
                    {isCurrent ? "Current Plan" : "Upgrade"}
                  </button>
                </div>
              );
            })}
          </div>
          {paymentInfo?.bank_transfer_enabled && paymentInfo?.bank_account_number && (
            <div className="mt-5 p-4 rounded-xl bg-amber-50 border border-amber-200">
              <div className="font-bold text-amber-800 text-sm">Or pay via Bank Transfer</div>
              <div className="text-xs text-amber-900 mt-1">
                <strong>{paymentInfo.bank_name}</strong> · {paymentInfo.bank_account_holder} · <span className="font-mono">{paymentInfo.bank_account_number}</span>
              </div>
              <div className="text-xs text-amber-800/80 mt-1">{paymentInfo.bank_instructions}</div>
            </div>
          )}
        </Card>
      )}

      <Card className="p-5 mb-6">
        <SectionHeader icon={BarChart3} title="Usage" hint="Your current plan limits" />
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {usageRows.map((u) => (
            <div key={u.lbl} className="p-4 rounded-xl border border-black/5">
              <div className="text-xs uppercase text-black/55">{u.lbl}</div>
              <div className="text-2xl font-extrabold mt-1">{u.used}{u.limit !== -1 && u.limit !== undefined && <span className="text-sm text-black/45 font-normal">/{u.limit}</span>}</div>
              {u.limit !== -1 && u.limit !== undefined && (
                <div className="w-full h-1.5 bg-black/5 rounded-full overflow-hidden mt-2">
                  <div className={`h-full ${pct(u.used, u.limit) > 80 ? "bg-rose-500" : "bg-[#1FA868]"}`} style={{ width: `${pct(u.used, u.limit)}%` }} />
                </div>
              )}
              {u.limit === -1 && <div className="text-xs text-emerald-700 font-semibold mt-1">Unlimited</div>}
            </div>
          ))}
        </div>
      </Card>

      <Card>
        <div className="p-4 border-b border-black/5 font-bold">Invoices</div>
        <table className="w-full text-sm">
          <thead className="bg-[#FAFBFB] text-black/55 text-[11px] uppercase">
            <tr>
              <th className="text-left p-3.5">Reference</th>
              <th className="text-left p-3.5">Date</th>
              <th className="text-left p-3.5">Description</th>
              <th className="text-right p-3.5">Amount</th>
              <th className="text-left p-3.5">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            {invoices.map((inv) => (
              <tr key={inv.id}>
                <td className="p-3.5 font-mono font-semibold">{inv.id}</td>
                <td className="p-3.5">{(inv.date || "").slice(0, 10)}</td>
                <td className="p-3.5">{inv.description}</td>
                <td className="p-3.5 text-right font-semibold">{fmt(inv.amount)}</td>
                <td className="p-3.5"><span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-emerald-100 text-emerald-700">{inv.status}</span></td>
              </tr>
            ))}
            {!invoices.length && <tr><td colSpan={5} className="p-10 text-center text-black/45">No invoices yet</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// ────────────────────────── REPORTS ──────────────────────────
export function ReportsHub() {
  const { subdomain } = useParams();
  const tiles = [
    { lbl: "Sales Report", desc: "View & export sales", to: "reports/sales", icon: Receipt, color: "from-emerald-500 to-emerald-600" },
    { lbl: "Purchase Report", desc: "Supplier purchases", to: "reports/purchases", icon: ShoppingCart, color: "from-sky-500 to-sky-600" },
    { lbl: "Stock Report", desc: "Inventory valuation", to: "reports/stock", icon: Package, color: "from-amber-500 to-amber-600" },
    { lbl: "Profit Report", desc: "Revenue vs cost", to: "reports/profit", icon: TrendingUp, color: "from-violet-500 to-violet-600" },
  ];
  return (
    <div data-testid="reports-hub-page" className="p-6 lg:p-8">
      <PageHeader title="Reports" breadcrumb="Reports" />
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {tiles.map((t) => {
          const Icon = t.icon;
          return (
            <Link key={t.lbl} to={`/t/${subdomain}/${t.to}`}
              className="group bg-white rounded-2xl border border-black/[0.06] p-6 hover:border-[#1FA868]/40 transition-colors">
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${t.color} text-white flex items-center justify-center`}><Icon size={22} /></div>
              <div className="mt-5 text-lg font-extrabold">{t.lbl}</div>
              <div className="text-xs text-black/55 mt-1">{t.desc}</div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function toCSV(rows, columns) {
  if (!rows?.length) return "";
  const header = columns.map((c) => c.lbl).join(",");
  const body = rows.map((r) => columns.map((c) => {
    const v = c.value ? c.value(r) : r[c.key];
    if (v == null) return "";
    const s = String(v).replace(/"/g, '""');
    return s.includes(",") || s.includes("\n") ? `"${s}"` : s;
  }).join(",")).join("\n");
  return header + "\n" + body;
}
function downloadCSV(name, csv) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
}

function DateRange({ start, setStart, end, setEnd, onApply }) {
  return (
    <div className="flex gap-2 items-center">
      <TextInput type="date" value={start} onChange={setStart} testid="rep-start" />
      <TextInput type="date" value={end} onChange={setEnd} testid="rep-end" />
      <PrimaryButton onClick={onApply} testid="rep-apply">Apply</PrimaryButton>
    </div>
  );
}

export function ReportSales() {
  const [start, setStart] = useState(""); const [end, setEnd] = useState("");
  const [d, setD] = useState({ rows: [], totals: {} });
  const load = () => api.get("/t/reports/sales", { params: { start_date: start || undefined, end_date: end || undefined } }).then((r) => setD(r.data));
  useEffect(() => { load(); }, []); // eslint-disable-line
  const exportCSV = () => {
    const cols = [
      { key: "reference", lbl: "Reference" },
      { key: "created_at", lbl: "Date", value: (r) => (r.created_at || "").slice(0, 10) },
      { key: "customer_name", lbl: "Customer" },
      { key: "total", lbl: "Total" },
      { key: "paid", lbl: "Paid" },
      { key: "due", lbl: "Due" },
      { key: "payment_status", lbl: "Status" },
    ];
    downloadCSV(`sales-report-${new Date().toISOString().slice(0, 10)}.csv`, toCSV(d.rows, cols));
  };
  return (
    <div data-testid="report-sales-page" className="p-6 lg:p-8">
      <PageHeader title="Sales Report" breadcrumb="Reports / Sales" actions={
        <><DateRange start={start} setStart={setStart} end={end} setEnd={setEnd} onApply={load} /><button onClick={exportCSV} data-testid="rep-sales-export" className="inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-xl bg-white border border-black/10 text-sm font-semibold"><Download size={13} /> Export CSV</button></>
      } />
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        {[{ lbl: "Count", v: d.totals.count }, { lbl: "Gross", v: fmt(d.totals.gross) }, { lbl: "Tax", v: fmt(d.totals.tax) }, { lbl: "Paid", v: fmt(d.totals.paid) }, { lbl: "Due", v: fmt(d.totals.due) }].map((s) => (
          <Card key={s.lbl} className="p-4"><div className="text-xs uppercase text-black/55">{s.lbl}</div><div className="text-xl font-extrabold mt-1">{s.v ?? 0}</div></Card>
        ))}
      </div>
      <Card>
        <table className="w-full text-sm">
          <thead className="bg-[#FAFBFB] text-black/55 text-[11px] uppercase">
            <tr><th className="text-left p-3.5">Reference</th><th className="text-left p-3.5">Date</th><th className="text-left p-3.5">Customer</th><th className="text-right p-3.5">Total</th><th className="text-right p-3.5">Paid</th><th className="text-right p-3.5">Due</th><th className="text-left p-3.5">Status</th></tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            {d.rows.map((r) => (
              <tr key={r.id}><td className="p-3.5 font-mono font-semibold">{r.reference}</td><td className="p-3.5">{(r.created_at || "").slice(0, 10)}</td><td className="p-3.5">{r.customer_name}</td><td className="p-3.5 text-right font-semibold">{fmt(r.total)}</td><td className="p-3.5 text-right">{fmt(r.paid)}</td><td className="p-3.5 text-right text-rose-700">{fmt(r.due)}</td><td className="p-3.5"><span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-black/5">{r.payment_status}</span></td></tr>
            ))}
            {!d.rows.length && <tr><td colSpan={7} className="p-10 text-center text-black/45">No sales in range</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

export function ReportPurchases() {
  const [start, setStart] = useState(""); const [end, setEnd] = useState("");
  const [d, setD] = useState({ rows: [], totals: {} });
  const load = () => api.get("/t/reports/purchases", { params: { start_date: start || undefined, end_date: end || undefined } }).then((r) => setD(r.data));
  useEffect(() => { load(); }, []); // eslint-disable-line
  const exportCSV = () => downloadCSV(`purchases-report-${new Date().toISOString().slice(0, 10)}.csv`, toCSV(d.rows, [{ key: "reference", lbl: "Reference" }, { key: "date", lbl: "Date" }, { key: "supplier_name", lbl: "Supplier" }, { key: "total", lbl: "Total" }, { key: "paid", lbl: "Paid" }, { key: "due", lbl: "Due" }]));
  return (
    <div data-testid="report-purchases-page" className="p-6 lg:p-8">
      <PageHeader title="Purchase Report" breadcrumb="Reports / Purchases" actions={
        <><DateRange start={start} setStart={setStart} end={end} setEnd={setEnd} onApply={load} /><button onClick={exportCSV} data-testid="rep-p-export" className="inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-xl bg-white border border-black/10 text-sm font-semibold"><Download size={13} /> Export CSV</button></>
      } />
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        {[{ lbl: "Count", v: d.totals.count }, { lbl: "Gross", v: fmt(d.totals.gross) }, { lbl: "Tax", v: fmt(d.totals.tax) }, { lbl: "Paid", v: fmt(d.totals.paid) }, { lbl: "Due", v: fmt(d.totals.due) }].map((s) => (
          <Card key={s.lbl} className="p-4"><div className="text-xs uppercase text-black/55">{s.lbl}</div><div className="text-xl font-extrabold mt-1">{s.v ?? 0}</div></Card>
        ))}
      </div>
      <Card>
        <table className="w-full text-sm">
          <thead className="bg-[#FAFBFB] text-black/55 text-[11px] uppercase">
            <tr><th className="text-left p-3.5">Reference</th><th className="text-left p-3.5">Date</th><th className="text-left p-3.5">Supplier</th><th className="text-right p-3.5">Total</th><th className="text-right p-3.5">Paid</th><th className="text-right p-3.5">Due</th></tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            {d.rows.map((r) => (<tr key={r.id}><td className="p-3.5 font-mono font-semibold">{r.reference}</td><td className="p-3.5">{r.date}</td><td className="p-3.5">{r.supplier_name}</td><td className="p-3.5 text-right font-semibold">{fmt(r.total)}</td><td className="p-3.5 text-right">{fmt(r.paid)}</td><td className="p-3.5 text-right text-rose-700">{fmt(r.due)}</td></tr>))}
            {!d.rows.length && <tr><td colSpan={6} className="p-10 text-center text-black/45">No purchases</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

export function ReportStock() {
  const [d, setD] = useState({ rows: [], totals: {} });
  useEffect(() => { api.get("/t/reports/stock").then((r) => setD(r.data)); }, []);
  const exportCSV = () => downloadCSV("stock-report.csv", toCSV(d.rows, [{ key: "code", lbl: "Code" }, { key: "name", lbl: "Product" }, { key: "category", lbl: "Category" }, { key: "stock", lbl: "Stock" }, { key: "unit_price", lbl: "Price" }, { key: "cost_price", lbl: "Cost" }]));
  return (
    <div data-testid="report-stock-page" className="p-6 lg:p-8">
      <PageHeader title="Stock Report" breadcrumb="Reports / Stock" actions={
        <button onClick={exportCSV} data-testid="rep-stock-export" className="inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-xl bg-white border border-black/10 text-sm font-semibold"><Download size={13} /> Export CSV</button>
      } />
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        {[{ lbl: "Products", v: d.totals.count }, { lbl: "Total Units", v: d.totals.units }, { lbl: "Value @ Cost", v: fmt(d.totals.value_at_cost) }, { lbl: "Value @ Sale", v: fmt(d.totals.value_at_sale) }, { lbl: "Low Stock (<10)", v: d.totals.low_stock }].map((s) => (
          <Card key={s.lbl} className="p-4"><div className="text-xs uppercase text-black/55">{s.lbl}</div><div className="text-xl font-extrabold mt-1">{s.v ?? 0}</div></Card>
        ))}
      </div>
      <Card>
        <table className="w-full text-sm">
          <thead className="bg-[#FAFBFB] text-black/55 text-[11px] uppercase">
            <tr><th className="text-left p-3.5">Code</th><th className="text-left p-3.5">Product</th><th className="text-left p-3.5">Category</th><th className="text-right p-3.5">Stock</th><th className="text-right p-3.5">Cost</th><th className="text-right p-3.5">Price</th><th className="text-right p-3.5">Value @ Cost</th></tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            {d.rows.map((r, i) => (<tr key={i} className={Number(r.stock || 0) < 10 ? "bg-rose-50/40" : ""}><td className="p-3.5 font-mono">{r.code}</td><td className="p-3.5 font-semibold">{r.name}</td><td className="p-3.5 text-black/60">{r.category || "—"}</td><td className="p-3.5 text-right font-bold">{r.stock}</td><td className="p-3.5 text-right">{fmt(r.cost_price)}</td><td className="p-3.5 text-right">{fmt(r.unit_price)}</td><td className="p-3.5 text-right font-semibold">{fmt((r.stock || 0) * (r.cost_price || 0))}</td></tr>))}
            {!d.rows.length && <tr><td colSpan={7} className="p-10 text-center text-black/45">No products</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

export function ReportProfit() {
  const [start, setStart] = useState(""); const [end, setEnd] = useState("");
  const [d, setD] = useState({});
  const load = () => api.get("/t/reports/profit", { params: { start_date: start || undefined, end_date: end || undefined } }).then((r) => setD(r.data));
  useEffect(() => { load(); }, []); // eslint-disable-line
  return (
    <div data-testid="report-profit-page" className="p-6 lg:p-8">
      <PageHeader title="Profit Report" breadcrumb="Reports / Profit" actions={
        <DateRange start={start} setStart={setStart} end={end} setEnd={setEnd} onApply={load} />
      } />
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <Card className="p-5"><div className="text-xs uppercase text-black/55">Gross Sales</div><div className="text-2xl font-extrabold mt-1 text-emerald-700">{fmt(d.gross)}</div></Card>
        <Card className="p-5"><div className="text-xs uppercase text-black/55">COGS</div><div className="text-2xl font-extrabold mt-1">{fmt(d.cogs)}</div></Card>
        <Card className="p-5"><div className="text-xs uppercase text-black/55">Expenses</div><div className="text-2xl font-extrabold mt-1 text-rose-700">{fmt(d.expense)}</div></Card>
        <Card className="p-5 bg-gradient-to-br from-[#1FA868] to-[#16834D] text-white"><div className="text-xs uppercase opacity-80">Net Profit</div><div className="text-2xl font-extrabold mt-1">{fmt(d.net_profit)}</div></Card>
      </div>
      <Card className="p-8">
        <div className="max-w-md mx-auto">
          <table className="w-full text-sm">
            <tbody className="divide-y divide-black/5">
              <tr><td className="p-3 text-black/60">Gross Revenue</td><td className="p-3 text-right font-semibold">{fmt(d.gross)}</td></tr>
              <tr><td className="p-3 text-black/60">(−) Cost of Goods Sold</td><td className="p-3 text-right font-semibold">{fmt(d.cogs)}</td></tr>
              <tr className="bg-[#FAFBFB] font-bold"><td className="p-3">Gross Profit</td><td className="p-3 text-right">{fmt(d.gross_profit)}</td></tr>
              <tr><td className="p-3 text-black/60">(−) Operating Expenses</td><td className="p-3 text-right font-semibold">{fmt(d.expense)}</td></tr>
              <tr className="bg-[#E8F5E9] font-extrabold text-[#1FA868]"><td className="p-3">Net Profit</td><td className="p-3 text-right">{fmt(d.net_profit)}</td></tr>
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
